// NOTE (Jul-9): this /api/edge client surface is KEEPING its contract, but
// the relay side is swapping the g10edge Go supervisor for an in-relay bot
// host (docs/bot-host-port-plan.md). Build new bot UI against this contract;
// never against the supervisor directly.
/**
 * Shared edge-bot wire: the canonical agent telemetry shapes, the trader-facing
 * mode → edge-kind mapping, client-side config resolution (personal → org →
 * built-in, mirroring the relay's resolveKindConfig), and the spawn/claim
 * helpers. Used by the Value Dashboard spawn flow, the presence markers, and the
 * Position Tracker bot zones — one source of truth for the /api/edge surface.
 */
import { apiFetch } from '@/stores/authStore';
import type { BotDefaults, BotKindConfig } from './types';
import { BOT_KIND_SPECS } from './configSpec';

// ── agent telemetry (mirrors g10edge agent.Stats; owner added relay-side) ──
export interface RestingOrder {
  id: string;
  side: 'bid' | 'ask';
  priceCents: number;
  sizeContracts: number;
  queuePosition: number; // contracts ahead; -1 = unknown
  queueSource?: string; // direct | hydra | orders | none
}
export interface MarkoutStat {
  horizonMs: number;
  fills: number;
  rightSideRate: number;
  avgCents: number;
}
export interface AgentStats {
  id: string;
  ticker: string;
  kind: string;
  mode: string;
  paused: boolean;
  posture: string; // green | yellow | orange | red
  haltReason?: string;
  lastFairCents: number;
  netInventory: number;
  lineVelocityCentsPerSec?: number;
  depthSource?: string;
  queueStatus?: string;
  queueStatusReason?: string;
  markout?: MarkoutStat[];
  resting: RestingOrder[] | null;
  loops: number;
  placed: number;
  canceled: number;
  errors: number;
  startedAtMs: number;
  owner?: { id: string; username: string } | null;
  /** Server-computed authz for the viewer (org-membership gated; admin role does
   *  NOT bypass). `canClaim` = may stake this bot (member of its org); `canOperate`
   *  = may pause/resume/reband/kill (member, and for a trader also the owner).
   *  Absent on older relay responses → treat as permissive fallback. */
  canClaim?: boolean;
  canOperate?: boolean;
}

export interface EdgeAgentsResponse {
  agents: AgentStats[];
  dryRun?: boolean;
}

// ── trader-facing bot modes (the Value Dashboard mode-row) → edge kind ──
export type TraderBotMode = 'vanilla' | 'recycler';
export interface TraderBotKind {
  mode: TraderBotMode;
  kind: string;
  label: string;
}
export const TRADER_BOT_KINDS: TraderBotKind[] = [
  { mode: 'vanilla', kind: 'vanillamm', label: 'Vanilla' },
  { mode: 'recycler', kind: 'queuerecycler', label: 'Recycler' },
];
export function kindForMode(mode: TraderBotMode): string {
  return TRADER_BOT_KINDS.find((k) => k.mode === mode)?.kind ?? 'vanillamm';
}

/** The contract-size field a given kind exposes in the minimal spawn form. */
export function sizeFieldForKind(kind: string): string {
  return BOT_KIND_SPECS.find((s) => s.kind === kind)?.primary[0] ?? 'sizeContracts';
}

/**
 * Resolve effective config for a kind: org profile → org byKind → personal
 * byKind (later wins). Returns only set fields; unset fall through to the edge
 * built-in. Mirrors relay/src/services/botConfig.ts resolveKindConfig.
 */
export function resolveBotConfig(
  kind: string,
  personal: BotDefaults | null | undefined,
  org: BotDefaults | null | undefined,
): BotKindConfig {
  const orgProfile = (org?.profile && org.profiles?.[org.profile]?.[kind]) || {};
  const orgByKind = org?.byKind?.[kind] || {};
  const personalByKind = personal?.byKind?.[kind] || {};
  return { ...orgProfile, ...orgByKind, ...personalByKind };
}

export interface SpawnResult {
  ok: boolean;
  status: number;
  id?: string;
  /** structured "a bot already exists here" (BOT_EXISTS) → offer claim. */
  existingId?: string;
  error?: string;
  code?: string;
}

/**
 * Spawn a bot on a market. Defaults to GUARD mode (quote_for_rebate, post-only);
 * pass mode:'cross_for_fills' to "let it ride" (take fills) from spawn. Mode is
 * an INDEPENDENT lever from claiming. `config` should already carry the resolved
 * defaults + the form's size.
 */
export async function spawnBot(args: {
  ticker: string;
  kind: string;
  config: BotKindConfig;
  intervalMs?: number;
  mode?: 'quote_for_rebate' | 'cross_for_fills';
}): Promise<SpawnResult> {
  const config: Record<string, unknown> = { ...args.config };
  // queuerecycler quotes a sides array; default to two-sided presence.
  if (args.kind === 'queuerecycler' && config.sides === undefined) {
    config.sides = ['bid', 'ask'];
  }
  try {
    const res = await apiFetch('/api/edge/agents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ticker: args.ticker,
        kind: args.kind,
        mode: args.mode ?? 'quote_for_rebate',
        intervalMs: args.intervalMs ?? 250,
        config,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, status: res.status, error: data.error, code: data.code, existingId: data.existingId };
    }
    return { ok: true, status: res.status, id: data.id };
  } catch (e) {
    return { ok: false, status: 0, error: (e as Error).message };
  }
}

export interface ClaimResult {
  ok: boolean;
  /** 'ALREADY_CLAIMED' when another trader holds it — surface to the user. */
  code?: string;
  error?: string;
}

/** Claim a bot under the current trader (relay-side attribution). Returns the
 *  structured outcome (not a bare boolean) so a lost claim (409 ALREADY_CLAIMED)
 *  can be shown to the user instead of silently no-op'ing. */
export async function claimBot(id: string, ticker?: string): Promise<ClaimResult> {
  try {
    const res = await apiFetch(`/api/edge/agents/${encodeURIComponent(id)}/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ticker ? { ticker } : {}),
    });
    if (res.ok) return { ok: true };
    const data = await res.json().catch(() => ({}));
    return { ok: false, code: data.code, error: data.error };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

/** Release a claimed bot back to the org pool. */
export async function releaseBot(id: string): Promise<boolean> {
  try {
    const res = await apiFetch(`/api/edge/agents/${encodeURIComponent(id)}/release`, { method: 'POST' });
    return res.ok;
  } catch {
    return false;
  }
}

/** Raw per-bot stop — the SAME endpoint the per-row kill uses, so server-side
 *  authz applies unchanged (a viewer who can't operate this bot gets a 403 →
 *  ok:false). No refresh side-effect, so a caller stopping many bots refreshes
 *  once at the end. Kill-all is a client loop over THIS, not a new batch surface,
 *  so it inherits the per-bot org/owner gating for free. */
export async function deleteAgent(id: string): Promise<boolean> {
  try {
    const res = await apiFetch(`/api/edge/agents/${encodeURIComponent(id)}`, { method: 'DELETE' });
    return res.ok;
  } catch {
    return false;
  }
}

export interface KillAllResult {
  attempted: number;
  succeeded: number;
  failed: number;
  /** ids that returned not-ok (403 for a foreign bot, or a transport failure) —
   *  surfaced so the operator knows exactly which bots may still be resting. */
  failedIds: string[];
}

/**
 * Stop every bot in `ids`, one venue call each at bounded concurrency, returning
 * a per-bot tally. NEVER reports a blanket "exposure is flat": today's per-bot
 * kill is itself best-effort on the edge (fire-and-forget; the kill-verify
 * follow-up is tracked separately), so the honest signal is "stopped X of N,
 * these Y may still be resting." The caller must pre-filter `ids` to
 * operable+visible bots so this never fires a guaranteed-403.
 */
export async function killAllBots(
  ids: readonly string[],
  killOne: (id: string) => Promise<boolean> = deleteAgent,
  opts?: { concurrency?: number },
): Promise<KillAllResult> {
  const unique = [...new Set(ids)];
  const failedIds: string[] = [];
  let succeeded = 0;
  if (unique.length === 0) return { attempted: 0, succeeded: 0, failed: 0, failedIds };
  // Small pool — the edge sits on a rate-budgeted channel and fleets are tiny;
  // this keeps the burst gentle and deterministic without going fully serial.
  const concurrency = Math.max(1, Math.min(opts?.concurrency ?? 3, unique.length));
  let cursor = 0;
  async function worker() {
    while (cursor < unique.length) {
      const id = unique[cursor++];
      const ok = await killOne(id).catch(() => false);
      if (ok) succeeded += 1;
      else failedIds.push(id);
    }
  }
  await Promise.all(Array.from({ length: concurrency }, () => worker()));
  return { attempted: unique.length, succeeded, failed: failedIds.length, failedIds };
}
